// Browser file sync module — uses localStorage for persistence + Firestore cloud backup
import type { MathZone, FileSyncState, StoredDocument } from "../../src/triggers/types";
import { getAuth } from "firebase/auth";
import { saveDocImages, deleteDocImages } from "./image-store";
export { loadDocImages } from "./image-store";

export type { StoredDocument };

import type { CloudSync } from "./cloud-sync";
import { trackEngagement } from "./usage-tracker";

// Injected by cloud-sync when user signs in (avoids circular deps)
let cloudSync: CloudSync | null = null;
export function setCloudSync(cs: CloudSync | null): void {
    cloudSync = cs;
}
export function getCloudSync(): CloudSync | null {
    return cloudSync;
}

const STORAGE_KEY = "seetex-documents";
const ACTIVE_DOC_KEY = "seetex-active-doc";
const CONTENT_KEY_PREFIX = "seetex-doc-content-";

/**
 * Read content for a single document.
 * Fast path: dedicated localStorage key (post-migration).
 * Fallback: reads from the old monolithic store blob (pre-migration, self-healing).
 */
export function getDocumentContent(docId: string): string {
    const dedicated = localStorage.getItem(CONTENT_KEY_PREFIX + docId);
    if (dedicated !== null) return dedicated;
    // Fallback for existing users whose content is still in the old store format
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (data) {
            const store = JSON.parse(data) as { documents: Array<{ id: string; content?: string }> };
            const doc = store.documents.find((d) => d.id === docId);
            if (doc?.content) return doc.content;
        }
    } catch { /* ignore */ }
    return "";
}

function saveDocumentContent(docId: string, content: string): void {
    try {
        localStorage.setItem(CONTENT_KEY_PREFIX + docId, content);
    } catch (e) {
        console.error("Failed to save document content:", e);
    }
}

function deleteDocumentContent(docId: string): void {
    localStorage.removeItem(CONTENT_KEY_PREFIX + docId);
}

/**
 * Wipe all locally-cached documents and active-doc pointer.
 * Call this on account deletion so stale data doesn't persist.
 */
export function clearAllLocalData(): void {
    // Remove per-doc content keys before clearing the index
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (data) {
            const store = JSON.parse(data) as { documents: Array<{ id: string }> };
            for (const doc of store.documents) {
                deleteDocumentContent(doc.id);
            }
        }
    } catch { /* best-effort */ }
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(ACTIVE_DOC_KEY);
}

interface DocumentStore {
    documents: StoredDocument[];
    version: number;
}

function getStore(): DocumentStore {
    try {
        const data = localStorage.getItem(STORAGE_KEY);
        if (data) {
            const store: DocumentStore = JSON.parse(data);
            // One-time migration: move inline content to per-doc keys so the
            // document index stays small and startup parsing is fast.
            let migrated = false;
            for (const doc of store.documents) {
                if (doc.content) {
                    saveDocumentContent(doc.id, doc.content);
                    doc.content = "";
                    migrated = true;
                }
            }
            if (migrated) {
                try { localStorage.setItem(STORAGE_KEY, JSON.stringify(store)); } catch { /* storage full */ }
            }
            return store;
        }
    } catch (e) {
        if (import.meta.env.DEV) console.warn("Failed to load documents from localStorage:", e);
    }
    return { documents: [], version: 1 };
}

function saveStore(store: DocumentStore): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (e) {
        console.error("Failed to save documents to localStorage:", e);
    }
}

/**
 * List all saved documents
 */
export function listDocuments(): StoredDocument[] {
    return getStore().documents;
}

/**
 * Get the active document ID
 */
export function getActiveDocumentId(): string | null {
    return localStorage.getItem(ACTIVE_DOC_KEY);
}

/**
 * Set the active document
 */
export function setActiveDocumentId(docId: string | null): void {
    if (docId) {
        localStorage.setItem(ACTIVE_DOC_KEY, docId);
    } else {
        localStorage.removeItem(ACTIVE_DOC_KEY);
    }
}

/**
 * Open/create a document by ID
 */
export async function openTexFile(docId: string): Promise<MathZone[]> {
    const store = getStore();
    const doc = store.documents.find((d) => d.id === docId);

    if (doc) {
        setActiveDocumentId(docId);
        return doc.mathZones;
    }

    // Create new document with sample content
    const newDoc: StoredDocument = {
        id: docId,
        name: docId,
        content: "",
        mathZones: parseMathZones(defaultFileContents),
        lastModified: Date.now(),
    };

    saveDocumentContent(docId, defaultFileContents);
    store.documents.push(newDoc);
    saveStore(store);
    setActiveDocumentId(docId);

    return newDoc.mathZones;
}

/**
 * Update a specific math zone in the document
 */
export async function updateMathZone(
    docId: string,
    zoneId: string,
    newLatex: string
): Promise<boolean> {
    const store = getStore();
    const doc = store.documents.find((d) => d.id === docId);

    if (!doc) return false;

    const zone = doc.mathZones.find((z) => z.id === zoneId);
    if (zone) {
        zone.latex = newLatex;
        doc.lastModified = Date.now();
        saveStore(store);
    }

    return true;
}

/**
 * Save document content. Images go to IndexedDB — never localStorage or Firestore.
 */
export async function saveFile(docId: string, content?: string, images?: Record<string, string>, isPro = false): Promise<boolean> {
    const store = getStore();
    const doc = store.documents.find((d) => d.id === docId);
    if (!doc) return false;

    if (content) {
        saveDocumentContent(docId, content);
        doc.mathZones = parseMathZones(content);
    }
    // Images are stored in IndexedDB/Firebase Storage, not in the document object.
    // Strip any in-memory images blob but record the filenames so Firestore knows
    // what to fetch on a new device.
    delete doc.images;
    if (images && Object.keys(images).length > 0) {
        doc.imageFiles = Object.keys(images);
    }
    doc.lastModified = Date.now();
    saveStore(store);
    trackEngagement();
    // Save images to IndexedDB + Firebase Storage (fire-and-forget)
    if (images && Object.keys(images).length > 0) {
        saveDocImages(docId, images, isPro).catch(() => { });
    }

    // Push doc to Firestore with full content for cross-device sync
    if (cloudSync) {
        const fullContent = content ?? getDocumentContent(docId);
        cloudSync.saveDocument({ ...doc, content: fullContent }).catch(() => { });
    }
    return true;
}

/**
 * Get the full file contents
 */
export async function readFileContents(docId: string): Promise<string> {
    return getDocumentContent(docId) || defaultFileContents;
}

/**
 * Create a new document (and cloud-sync when signed in)
 */
export function createDocument(name: string): StoredDocument {
    const store = getStore();
    const id = `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const newDoc: StoredDocument = {
        id,
        name,
        content: defaultFileContents,
        mathZones: parseMathZones(defaultFileContents),
        lastModified: Date.now(),
    };

    saveDocumentContent(id, defaultFileContents);
    store.documents.push({ ...newDoc, content: "" });
    saveStore(store);
    setActiveDocumentId(id);
    trackEngagement();
    cloudSync?.saveDocument(newDoc).catch(() => { });

    return newDoc;
}

/**
 * Delete a document (and cloud-sync when signed in)
 */
export function deleteDocument(docId: string): boolean {
    const store = getStore();
    const idx = store.documents.findIndex((d) => d.id === docId);

    if (idx === -1) return false;

    store.documents.splice(idx, 1);
    saveStore(store);
    deleteDocumentContent(docId);
    deleteDocImages(docId).catch(() => { });
    cloudSync?.deleteDocument(docId).catch(() => { });

    if (getActiveDocumentId() === docId) {
        setActiveDocumentId(store.documents[0]?.id ?? null);
    }

    return true;
}

/**
 * Rename a document
 */
export function renameDocument(docId: string, newName: string): boolean {
    const store = getStore();
    const doc = store.documents.find((d) => d.id === docId);

    if (!doc) return false;

    doc.name = newName;
    saveStore(store);
    return true;
}

/**
 * Download document as .tex file
 */
export function downloadAsTexFile(content: string, filename: string = "document.tex"): void {
    const blob = new Blob([content], { type: "application/x-tex" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = filename.endsWith(".tex") ? filename : `${filename}.tex`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
}

/**
 * Import a .tex file from user upload
 */
export async function importTexFile(file: File): Promise<StoredDocument> {
    const content = await file.text();
    const name = file.name.replace(/\.tex$/i, "");

    const store = getStore();
    const id = `doc-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    const newDoc: StoredDocument = {
        id,
        name,
        content,
        mathZones: parseMathZones(content),
        lastModified: Date.now(),
    };

    saveDocumentContent(id, content);
    store.documents.push({ ...newDoc, content: "" });
    saveStore(store);
    setActiveDocumentId(id);

    return newDoc; // return with content intact so callers can scan for \includegraphics
}

/**
 * Merge documents received from the cloud into localStorage.
 * Cloud wins on conflict if its lastModified is newer.
 * Returns true if any document was updated/added.
 */
export function mergeCloudDocuments(cloudDocs: StoredDocument[]): boolean {
    const store = getStore();
    let changed = false;
    for (const cloudDoc of cloudDocs) {
        const { content, ...meta } = cloudDoc;
        const storeEntry = { ...meta, content: "" };
        const idx = store.documents.findIndex((d) => d.id === cloudDoc.id);
        if (idx >= 0) {
            // Always restore content from cloud if local copy is missing
            if (content && !getDocumentContent(cloudDoc.id)) {
                saveDocumentContent(cloudDoc.id, content);
            }
            if (cloudDoc.lastModified > store.documents[idx].lastModified) {
                if (content) saveDocumentContent(cloudDoc.id, content);
                store.documents[idx] = storeEntry;
                changed = true;
            }
        } else {
            if (content) saveDocumentContent(cloudDoc.id, content);
            store.documents.push(storeEntry);
            changed = true;
        }
    }
    if (changed) saveStore(store);
    return changed;
}

/**
 * Create a new FileSyncState
 */
export function createFileSyncState(): FileSyncState {
    return {
        filePath: null,
        mathZones: [],
        activeZoneId: null,
        isDirty: false,
        lastSyncTime: Date.now(),
    };
}

/**
 * Parse math zones from LaTeX content (browser implementation)
 */
function parseMathZones(contents: string): MathZone[] {
    const zones: MathZone[] = [];
    let zoneId = 0;

    const lineAt = (offset: number): number =>
        contents.slice(0, offset).split("\n").length;

    // Inline math: $...$
    const inlineRegex = /(?<!\$)\$(?!\$)([^$]+)\$/g;
    let match: RegExpExecArray | null;

    while ((match = inlineRegex.exec(contents)) !== null) {
        const latex = match[1].trim();
        if (latex) {
            zoneId++;
            zones.push({
                id: `zone-${zoneId}`,
                latex,
                startOffset: match.index,
                endOffset: match.index + match[0].length,
                envType: "inline",
                line: lineAt(match.index),
            });
        }
    }

    // Display math: \[...\]
    const displayRegex = /\\\[([\s\S]*?)\\\]/g;
    while ((match = displayRegex.exec(contents)) !== null) {
        const latex = match[1].trim();
        if (latex) {
            zoneId++;
            zones.push({
                id: `zone-${zoneId}`,
                latex,
                startOffset: match.index,
                endOffset: match.index + match[0].length,
                envType: "display",
                line: lineAt(match.index),
            });
        }
    }

    // Equation environment
    const eqRegex = /\\begin\{equation\}([\s\S]*?)\\end\{equation\}/g;
    while ((match = eqRegex.exec(contents)) !== null) {
        const latex = match[1].trim();
        if (latex) {
            zoneId++;
            zones.push({
                id: `zone-${zoneId}`,
                latex,
                startOffset: match.index,
                endOffset: match.index + match[0].length,
                envType: "equation",
                line: lineAt(match.index),
            });
        }
    }

    // Align environments
    const alignRegex = /\\begin\{(align\*?)\}([\s\S]*?)\\end\{\1\}/g;
    while ((match = alignRegex.exec(contents)) !== null) {
        const latex = match[2].trim();
        if (latex) {
            zoneId++;
            zones.push({
                id: `zone-${zoneId}`,
                latex,
                startOffset: match.index,
                endOffset: match.index + match[0].length,
                envType: match[1],
                line: lineAt(match.index),
            });
        }
    }

    return zones.sort((a, b) => a.startOffset - b.startOffset);
}

/**
 * Compile LaTeX to PDF via the local compilation server.
 * Falls back to .tex download if the server is unreachable.
 */
export async function compileToPdf(latexContent: string, images?: Record<string, string>): Promise<string> {
    const titleMatch = latexContent.match(/\\title\{([^}]*)\}/);
    const filename = titleMatch ? titleMatch[1].trim() || "document" : "document";

    const apiBase = (import.meta.env as Record<string, string>).VITE_COMPILE_API_URL ?? "/api";

    const token = await getAuth().currentUser?.getIdToken();
    let response: Response;
    try {
        response = await fetch(`${apiBase}/compile-pdf`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                ...(token ? { "Authorization": `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ latex: latexContent, filename, ...(images && Object.keys(images).length > 0 ? { images } : {}) }),
        });
    } catch {
        // Server unreachable — fall back
        downloadAsTexFile(latexContent, filename);
        throw new Error(
            "PDF server not running. Start it with: npm run dev:server\n" +
            "Your .tex file has been downloaded as a fallback."
        );
    }

    if (!response.ok) {
        let msg = "Compilation failed";
        try {
            const json = await response.json() as { error?: string; log?: string };
            // Include the log context so auto-fix can extract line numbers
            msg = json.error ?? msg;
            if (json.log) msg += "\n" + json.log;
        } catch { /* ignore */ }
        throw new Error(msg);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    return `${filename}.pdf`;
}

// ── File System Access API ────────────────────────────────────────────────

/** Open a real .tex file from the local filesystem using the File System Access API */
export async function openFromFilesystem(): Promise<StoredDocument | null> {
    if (!("showOpenFilePicker" in window)) {
        throw new Error("File System Access API is not supported in this browser");
    }
    let fileHandles: FileSystemFileHandle[];
    try {
        fileHandles = await (window as unknown as {
            showOpenFilePicker: (opts: unknown) => Promise<FileSystemFileHandle[]>
        }).showOpenFilePicker({
            types: [{ description: "LaTeX files", accept: { "application/x-tex": [".tex"] } }],
            multiple: false,
        });
    } catch (e) {
        // User cancelled — not an error
        if ((e as Error).name === "AbortError") return null;
        throw e;
    }
    const file = await fileHandles[0].getFile();
    return importTexFile(file);
}

/** Save the given content to a real file on disk via the File System Access API */
export async function saveToFilesystem(content: string, suggestedName: string = "document.tex"): Promise<void> {
    if (!("showSaveFilePicker" in window)) {
        // Fallback to blob download
        downloadAsTexFile(content, suggestedName);
        return;
    }
    let handle: FileSystemFileHandle;
    try {
        handle = await (window as unknown as {
            showSaveFilePicker: (opts: unknown) => Promise<FileSystemFileHandle>
        }).showSaveFilePicker({
            suggestedName: suggestedName.endsWith(".tex") ? suggestedName : `${suggestedName}.tex`,
            types: [{ description: "LaTeX files", accept: { "application/x-tex": [".tex"] } }],
        });
    } catch (e) {
        if ((e as Error).name === "AbortError") return;
        throw e;
    }
    const writable = await handle.createWritable();
    await writable.write(content);
    await writable.close();
}

// ============================================================
// Default document content
// ============================================================
export const defaultFileContents = `\\documentclass{article}
\\usepackage{amsmath}
\\usepackage{amssymb}

\\title{My SeeTex Document}
\\author{Your Name}
\\date{\\today}

\\begin{document}

\\maketitle

\\section{Introduction}

Welcome to SeeTex! This is a visual LaTeX editor that lets you see your math as you type.

The quadratic formula is:
\\[
  x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}
\\]

\\section{Examples}

Euler's identity: $e^{i\\pi} + 1 = 0$

A beautiful integral:
\\begin{equation}
  \\int_0^\\infty e^{-x^2} \\, dx = \\frac{\\sqrt{\\pi}}{2}
\\end{equation}

Maxwell's equations:
\\begin{align*}
  \\nabla \\cdot \\mathbf{E} &= \\frac{\\rho}{\\epsilon_0} \\\\
  \\nabla \\cdot \\mathbf{B} &= 0 \\\\
  \\nabla \\times \\mathbf{E} &= -\\frac{\\partial \\mathbf{B}}{\\partial t} \\\\
  \\nabla \\times \\mathbf{B} &= \\mu_0 \\mathbf{J} + \\mu_0 \\epsilon_0 \\frac{\\partial \\mathbf{E}}{\\partial t}
\\end{align*}

\\end{document}`;
